import { Spinner } from '@chakra-ui/react';
import { BlueprintQuoteResponse, IQuoteRespSchema } from '@monetize/types/app';
import { DEFAULT_PAGER } from '@monetize/types/common';
import { useEffect, useState } from 'react';
import { MdOpenInNew } from 'react-icons/md';
import { handleApiErrorToast } from '../../../../api/axios';
import {
  useGetBlueprintById,
  useGetBlueprintQuotes,
  useUpdateQuoteBlueprint,
} from '../../../../api/cpqService';
import { BlueprintSelect } from '../../../../components/Blueprint/BlueprintSelect';
import {
  MBox,
  MButton,
  MCenter,
  MCenterModal,
  MFormField,
  MIcon,
  MLink,
  MStack,
  MText,
} from '../../../../components/Monetize';
import { getQuoteBaseRoute } from '../../../../constants/routes';
import { useConfirmModal } from '../../../../services/confirmModal';

interface ChangeQuoteBlueprintModalProps {
  quote: IQuoteRespSchema;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const ChangeQuoteBlueprintModal = ({
  quote,
  isOpen,
  onClose,
  onSuccess,
}: ChangeQuoteBlueprintModalProps) => {
  const blueprintId = quote.metadata?.bluePrintId || '';
  const sourceBluePrintQuoteId = quote.metadata?.sourceBluePrintQuoteId || '';
  const hasBlueprint = !!blueprintId || !!sourceBluePrintQuoteId;
  const [showBlueprintSelect, setShowBlueprintSelect] = useState(false);
  const [selectedBlueprintId, setSelectedBlueprintId] =
    useState<string>(blueprintId);
  const { showConfirmModal, setModalLoading, closeModal } = useConfirmModal();

  const { data: blueprintsData } = useGetBlueprintQuotes(
    {
      config: { ...DEFAULT_PAGER, rows: 100 },
      filters: quote?.currency
        ? { quoteCurrency: quote.currency, isActive: 'true' }
        : undefined,
    },
    {
      enabled: !blueprintId && !!sourceBluePrintQuoteId && !!quote?.currency,
    },
  );
  const foundBlueprintId =
    blueprintId ||
    blueprintsData?.content?.find(
      ({ quote }) => quote?.id === sourceBluePrintQuoteId,
    )?.id ||
    '';

  const { data: blueprint, isLoading: isLoadingBlueprint } =
    useGetBlueprintById(foundBlueprintId, { enabled: !!foundBlueprintId });

  const { mutate: updateBlueprint, isPending: isUpdating } =
    useUpdateQuoteBlueprint({
      onSuccess: () => {
        setModalLoading(false);
        handleClose();
        onSuccess?.();
      },
      onError: (err) => {
        setModalLoading(false);
        handleApiErrorToast(err);
      },
    });

  const handleBlueprintChange = (
    selectedBlueprint: BlueprintQuoteResponse | null,
  ) => {
    setSelectedBlueprintId(selectedBlueprint?.id || '');
  };

  const handleSave = () => {
    if (!selectedBlueprintId) {
      return;
    }

    const currentId = blueprintId;
    if (selectedBlueprintId === currentId) {
      onClose();
      return;
    }

    showConfirmModal({
      title: 'Do you want to use this blueprint?',
      description: 'It will replace all your products in the quote.',
      onYes: () => {
        setModalLoading(true);
        closeModal();
        updateBlueprint({
          quoteId: quote.id,
          blueprintId: selectedBlueprintId,
        });
      },
      yesBtnProps: { variant: 'primary' },
      noBtnProps: { variant: 'cancel' },
      yesButton: 'Confirm',
      noButton: 'Cancel',
    });
  };

  const handleClose = () => {
    setShowBlueprintSelect(false);
    setSelectedBlueprintId('');
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      setShowBlueprintSelect(false);
      setSelectedBlueprintId('');
      return;
    }
    setSelectedBlueprintId(foundBlueprintId);
    setShowBlueprintSelect(true);
  }, [isOpen, foundBlueprintId]);

  return (
    <MCenterModal
      size="md"
      isOpen={isOpen}
      onClose={handleClose}
      modalTitle={
        showBlueprintSelect ? 'Select Blueprint' : 'Blueprint Details'
      }
      renderFooter={
        showBlueprintSelect
          ? () => (
              <MStack
                gap={4}
                direction="row"
                align="center"
                justify="right"
                flex={1}
              >
                <MButton
                  variant="cancel"
                  isDisabled={isUpdating}
                  onClick={() => {
                    if (hasBlueprint) {
                      setShowBlueprintSelect(false);
                      setSelectedBlueprintId('');
                    } else {
                      handleClose();
                    }
                  }}
                  minW="auto"
                >
                  Cancel
                </MButton>
                <MButton
                  variant="primary"
                  isLoading={isUpdating}
                  onClick={handleSave}
                  isDisabled={!selectedBlueprintId || isUpdating}
                  minW="auto"
                >
                  Save
                </MButton>
              </MStack>
            )
          : null
      }
    >
      {isLoadingBlueprint ? (
        <MCenter>
          <Spinner />
        </MCenter>
      ) : (
        <MBox>
          {!showBlueprintSelect ? (
            <>
              {hasBlueprint && blueprint?.quote?.id && (
                <MFormField label="Quote" mb={4}>
                  <MLink
                    href={getQuoteBaseRoute(blueprint.quote.id)}
                    target="_blank"
                    display="flex"
                    alignItems="center"
                    fontSize="sm"
                  >
                    {blueprint.quote.description}
                    <MIcon as={MdOpenInNew} ml="1" color="tIndigo.base" />
                  </MLink>
                </MFormField>
              )}

              {hasBlueprint && blueprint && (
                <MFormField label="Blueprint Name" mb={4}>
                  <MText fontSize="sm">{blueprint.name}</MText>
                </MFormField>
              )}

              <MStack
                gap={4}
                direction="row"
                align="center"
                justify="right"
                flex={1}
              >
                <MButton variant="cancel" onClick={handleClose} minW="auto">
                  Cancel
                </MButton>
                <MButton
                  variant="primary"
                  onClick={() => setShowBlueprintSelect(true)}
                  minW="auto"
                >
                  Change Blueprint
                </MButton>
              </MStack>
            </>
          ) : (
            <MFormField label="Blueprint">
              <BlueprintSelect
                value={selectedBlueprintId}
                onChange={handleBlueprintChange}
                currency={quote.currency}
                clearable={false}
              />
            </MFormField>
          )}
        </MBox>
      )}
    </MCenterModal>
  );
};
